import React, { useMemo, useState } from "react";
import {
  Card,
  Button,
  Popconfirm,
  Space,
  Typography,
  Empty,
  Spin,
} from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteInvestmentThunk,
  getSingleInvestmentThunk,
} from "../../../../Store/Slices/Finance/investments/investment.thunk";
import InvestmentDetailsDrawer from "./InvestmentDetailsDrawer";
import { FiEye, FiEdit2, FiTrash2, FiTrendingUp } from "react-icons/fi";
import ProtectedAction from "../../../../Routes/ProtectedRoutes/ProtectedAction";
import { PERMISSIONS } from "../../../../config/permission";

const { Title, Text } = Typography;

const InvestmentTable = () => {
  const dispatch = useDispatch();
  const { investments, loading } = useSelector(
    (state) => state.admin.investment,
  );

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedInvestment, setSelectedInvestment] = useState(null);
  const [mode, setMode] = useState("");

  const openDrawer = async (record, drawerMode) => {
    const result = await dispatch(getSingleInvestmentThunk(record._id));
    if (!result.error) {
      setSelectedInvestment(result.payload?.data || record);
      setMode(drawerMode);
      setDrawerOpen(true);
    }
  };

  const handleDelete = (id) => {
    dispatch(deleteInvestmentThunk(id));
  };

  return (
    <div className="rounded-3xl min-h-screen">
      {loading ? (
        <div className="flex justify-center py-20">
          <Spin size="large" />
        </div>
      ) : !investments?.length ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex items-center justify-center py-20">
          <Empty description="No investments found" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {investments.map((record) => (
            <div
              key={record._id}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-purple-100 transition-all duration-200 flex flex-col overflow-hidden"
            >
              {/* Card Header */}
              <div className="p-5 border-b border-gray-100">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
                      <FiTrendingUp className="w-5 h-5 text-purple-500" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-bold text-gray-900 capitalize leading-tight truncate">
                        {record.title}
                      </h3>
                      <p className="text-xs text-gray-400 mt-0.5 truncate">
                        {record?.entityId?.entityName ||
                          record?.entityId?.name ||
                          "—"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-5 flex flex-col gap-3 flex-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Total Investment</span>
                  <span className="text-sm font-semibold text-gray-800">
                    {Number(record.totalInvestment || 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Profit Percentage</span>
                  <span className="inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider bg-purple-50 text-purple-600 border border-purple-100">
                    {record.totalPercentage || 0}%
                  </span>
                </div>
              </div>

              {/* Card Footer */}
              <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center gap-2">
                {/* View */}
                <button
                  onClick={() => openDrawer(record, "view")}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 transition"
                  title="View"
                >
                  <FiEye className="w-4 h-4" />
                </button>

                {/* Edit */}
                <ProtectedAction requiredPermission={PERMISSIONS.EDIT_SHARE_HOLDERS}>
                  <button
                    onClick={() => openDrawer(record, "edit")}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-green-500 hover:bg-green-50 transition"
                    title="Edit"
                  >
                    <FiEdit2 className="w-4 h-4" />
                  </button>
                </ProtectedAction>
                {/* Delete */}
                <ProtectedAction requiredPermission={PERMISSIONS.DELETE_SHARE_HOLDERS}>
                  <Popconfirm
                    title="Delete investment?"
                    onConfirm={() => handleDelete(record._id)}
                    okButtonProps={{ danger: true }}
                  >
                    <button
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition"
                      title="Delete"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </Popconfirm>
                </ProtectedAction>
              </div>
            </div>
          ))}
        </div>
      )}

      <InvestmentDetailsDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedInvestment(null);
        }}
        investment={selectedInvestment}
        mode={mode}
      />
    </div>
  );
};

export default InvestmentTable;